// Port of the date handling of CoreModels/Support/JSONCoding.swift.
//
// Dates are ISO 8601 in UTC. Whole-second instants encode without a fraction ("2026-06-21T10:00:00Z"), other instants
// keep millisecond precision ("2026-06-21T10:00:00.250Z"). Decoding accepts date-times with or without fractional
// seconds (with `Z` or an offset), plain dates (yyyy-MM-dd, midnight UTC), zone-less date-times without a fraction
// (assumed UTC, as some GPX writers emit them) and numbers (seconds since 1970).

import type { EpochMs } from './models';

const dateTimeWithZone = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/;
const zonelessDateTime = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;
const plainDate = /^(\d{4})-(\d{2})-(\d{2})$/;

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function utcInstant(y: number, mo: number, d: number, h: number, mi: number, s: number): EpochMs | undefined {
  if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo) || h > 23 || mi > 59 || s > 59) return undefined;
  const date = new Date(0);
  date.setUTCFullYear(y, mo - 1, d); // setUTCFullYear keeps years 0…99 literal (Date.UTC would map them to 19xx)
  date.setUTCHours(h, mi, s, 0);
  return date.getTime();
}

function offsetMinutes(zone: string): number | undefined {
  if (zone === 'Z') return 0;
  const sign = zone[0] === '-' ? -1 : 1;
  const digits = zone.slice(1).replace(':', '');
  const hours = Number(digits.slice(0, 2));
  const minutes = Number(digits.slice(2, 4));
  if (hours > 23 || minutes > 59) return undefined;
  return sign * (hours * 60 + minutes);
}

/** Parses ISO 8601 date-times (with/without fractional seconds, with Z or offset) and plain dates (Swift `parseDate`). */
export function parseDate(s: string): EpochMs | undefined {
  let m = dateTimeWithZone.exec(s);
  if (m) {
    const base = utcInstant(+m[1], +m[2], +m[3], +m[4], +m[5], +m[6]);
    const offset = offsetMinutes(m[8]);
    if (base === undefined || offset === undefined) return undefined;
    const fraction = m[7] ? Number(`0.${m[7]}`) * 1000 : 0;
    return base + fraction - offset * 60_000;
  }
  if (s.length === 10) {
    m = plainDate.exec(s);
    if (m) return utcInstant(+m[1], +m[2], +m[3], 0, 0, 0);
  }
  // Tolerate local times without zone designator (e.g. from some GPX writers) by assuming UTC.
  m = zonelessDateTime.exec(s);
  if (m) return utcInstant(+m[1], +m[2], +m[3], +m[4], +m[5], +m[6]);
  return undefined;
}

/** Decodes a JSON date value: a number of seconds since 1970 or an ISO 8601 string. Throws on anything else. */
export function decodeDateValue(value: unknown): EpochMs {
  if (typeof value === 'number' && Number.isFinite(value)) return value * 1000;
  if (typeof value === 'string') {
    const date = parseDate(value);
    if (date !== undefined) return date;
    throw new TypeError(`Unrecognised date: ${value}`);
  }
  throw new TypeError(`Expected a date, found ${value === null ? 'null' : typeof value}`);
}

/** Encodes an instant like Swift's `JSONCoding.makeEncoder()`: no fraction for whole seconds, else milliseconds. */
export function formatDate(date: EpochMs): string {
  const iso = new Date(date).toISOString();
  return Math.floor(date / 1000) * 1000 === date ? iso.replace(/\.\d{3}Z$/, 'Z') : iso;
}

function sortedKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortedKeysDeep);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = sortedKeysDeep(v);
    }
    return out;
  }
  return value;
}

/**
 * JSON text like Swift's `JSONCoding.makeEncoder(pretty:)`: keys sorted (`.sortedKeys`), slashes unescaped (as
 * `JSON.stringify` always does), 2-space indentation when `pretty`.
 */
export function stringifyJSON(value: unknown, pretty = false): string {
  return JSON.stringify(sortedKeysDeep(value), null, pretty ? 2 : undefined);
}
